/**
 * Give a signed-in Apple identity every Apple audience Morfi signs in with.
 *
 * Apple's `aud` differs per platform (iOS bundle id, web/Android Services ID)
 * and Turnkey matches an identity on (iss, aud, sub). A sub-organization
 * created by a sign-up that registered only its own platform's audience is
 * unreachable from the other platform: the same Apple ID lands in a new, empty
 * sub-organization there. Run with the session an Apple login just minted; it
 * adds each missing audience to the signed-in user.
 *
 * Turnkey's auth proxy cannot look an identity up by `oidcClaims`
 * ("unsupported filter type: OIDC_CLAIMS"), so whether another sub-organization
 * already holds an audience is left to Turnkey's own answer to the create.
 *
 * Platform-neutral — the kit's `httpClient` on web and React Native both
 * satisfy `AudienceClient`.
 */

import { decodeJwtPayload } from '../oauth/oauth-claims.js';
import { appleSecondaryOidcClaims } from './oauth-client-ids.js';

export interface AudienceClient {
  readonly getUser: (input: {
    readonly userId: string;
    readonly organizationId: string;
  }) => Promise<{
    readonly user: { readonly oauthProviders: readonly OauthProviderLike[] };
  }>;
  readonly createOauthProviders: (input: {
    readonly userId: string;
    readonly organizationId: string;
    readonly oauthProviders: Array<{ providerName: string; oidcClaims: OidcClaims }>;
  }) => Promise<unknown>;
}

interface OauthProviderLike {
  readonly issuer: string;
  readonly audience: string;
  readonly subject: string;
}

interface OidcClaims {
  readonly iss: string;
  readonly sub: string;
  readonly aud: string;
}

/** Auth event an Apple audience repair reports through `onAuthEvent`. */
export const APPLE_AUDIENCE_REPAIR_EVENT = 'turnkey_apple_audience_repair';

/**
 * After an Apple login or connect, add the identity's other Apple audiences to
 * the signed-in user and report what changed. `completion` carries the Turnkey
 * `sessionToken` (the kit's completion result, or `{ sessionToken }` from the
 * current session); the token names the user and sub-organization, so the
 * kit's `getSession` (whose failures surface through the kit's `onError`) is
 * never needed. Never throws: the login or connect already succeeded, so a
 * failure is only reported and retried on the next Apple login.
 */
export async function registerOtherAppleAudiences(input: {
  readonly client: AudienceClient | undefined;
  readonly completion: unknown;
  readonly oidcToken: string;
  readonly onAuthEvent?: ((name: string, payload: Record<string, unknown>) => void) | undefined;
}): Promise<void> {
  if (input.client === undefined) return;
  try {
    const session = sessionOf(input.completion);
    const added = await registerMissingAppleAudiences(input.client, {
      oidcToken: input.oidcToken,
      userId: session.userId,
      organizationId: session.organizationId,
    });
    if (added.length === 0) return;
    input.onAuthEvent?.(APPLE_AUDIENCE_REPAIR_EVENT, { outcome: 'added', added });
  } catch (err) {
    input.onAuthEvent?.(APPLE_AUDIENCE_REPAIR_EVENT, {
      outcome: 'failed',
      error_name: err instanceof Error ? err.name : typeof err,
      ...errorCode(err),
    });
  }
}

/** Adds the Apple audiences the user lacks; returns the ones added. */
export async function registerMissingAppleAudiences(
  client: AudienceClient,
  login: { readonly oidcToken: string; readonly userId: string; readonly organizationId: string },
): Promise<readonly string[]> {
  const wanted = appleSecondaryOidcClaims(login.oidcToken);
  if (wanted.length === 0) return [];

  const { user } = await client.getUser({
    userId: login.userId,
    organizationId: login.organizationId,
  });
  const missing = wanted.filter((claim) => !user.oauthProviders.some((p) => matches(p, claim)));
  if (missing.length === 0) return [];

  await client.createOauthProviders({
    userId: login.userId,
    organizationId: login.organizationId,
    oauthProviders: missing.map((oidcClaims) => ({ providerName: 'apple', oidcClaims })),
  });
  return missing.map((claim) => claim.aud);
}

/** The user and sub-organization in the Turnkey session token a completion carries. */
function sessionOf(completion: unknown): { userId: string; organizationId: string } {
  const token =
    typeof completion === 'object' && completion !== null && 'sessionToken' in completion
      ? completion.sessionToken
      : undefined;
  const claims = typeof token === 'string' ? decodeJwtPayload(token) : {};
  if (typeof claims.user_id !== 'string' || typeof claims.organization_id !== 'string') {
    throw new TypeError('OAuth completion carried no Turnkey session token');
  }
  return { userId: claims.user_id, organizationId: claims.organization_id };
}

/** Turnkey's machine-readable error code, when the error carries one. */
function errorCode(err: unknown): { error_code?: string } {
  if (typeof err !== 'object' || err === null || !('code' in err)) return {};
  const code = err.code;
  return typeof code === 'string' || typeof code === 'number' ? { error_code: String(code) } : {};
}

function matches(provider: OauthProviderLike, claim: OidcClaims): boolean {
  return (
    provider.issuer === claim.iss &&
    provider.subject === claim.sub &&
    provider.audience === claim.aud
  );
}
